import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useDisconnectAccountFromIntegrationMutation,
	useGetExternalIntegrationQuery,
	usePostDonorPerfectAuthMutation,
} from '../../../../queries';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import donorPerfectLogo from '../../../assets/logo_donorperfect.png';
import { Button } from '../../../components-v2/Button';
import { Input } from '../../../components-v2/Input';
import { Label } from '../../../components-v2/Label';
import { THEME } from '../../../components-v2/Theme/theme';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

const disconnectModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Disconnect',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

enum TabValues {
	Configure = 'configure',
	Manage = 'manage',
}

export function DonorPerfectV2() {
	const [apiKey, setApiKey] = React.useState('');
	const [isEditing, setIsEditing] = React.useState(false);
	const [isDisconnectModalOpen, setIsDisconnectModalOpen] = React.useState(false);
	const history = useHistory();
	const userSession = useUserSession();
	const adminUserSession = userSession as AdminUserSessionContext;
	const params = useParams<{ tab?: string }>();
	const impersonationContext = userSession.impersonationContext;

	const authMutation = usePostDonorPerfectAuthMutation({
		impersonationContext,
		onSuccess: () => {
			// While impersonating, the pending actions on this session belong to the admin, not the account being
			// configured, so clearing one here would dismiss the wrong dashboard card.
			if (!impersonationContext?.account?.id) {
				userSession.resolvePendingAction(Api.PendingActions.ConnectDonorPerfectV2);
			}
			invalidateIntegrationQuery({ impersonationContext, provider: Api.IntegrationProvider.DonorPerfect });
			toast.success('Successfully connected to DonorPerfect.');
			setApiKey('');
			setIsEditing(false);
		},
	});

	const disconnectMutation = useDisconnectAccountFromIntegrationMutation({
		impersonationContext,
		onError: error => {
			toast.error(error.systemMessage || 'Failed to disconnect DonorPerfect. Please try again.');
		},
		onSuccess: () => {
			invalidateIntegrationQuery({ impersonationContext, provider: Api.IntegrationProvider.DonorPerfect });
			toast.success('DonorPerfect has been disconnected.');
		},
	});

	const onDisconnectModalRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		setIsDisconnectModalOpen(false);
		if (result?.isDestructive && !canceled) {
			disconnectMutation.mutate(Api.ExternalIntegrationType.DonorPerfect);
		}
	};

	const integrationQuery = useGetExternalIntegrationQuery({
		impersonationContext,
		provider: Api.IntegrationProvider.DonorPerfect,
	});

	const configuration = integrationQuery.data?.configuration;
	const isConnected = !!configuration?.accountConnection?.isConnected;
	const needsConfiguration = configuration?.needsConfiguration !== false;

	const changeTab = React.useCallback(
		(value: string) => {
			history.replace({
				...history.location,
				pathname: `/integrations/${Api.IntegrationProvider.DonorPerfect}/${value}`,
			});
		},
		[history]
	);

	const canConfigure = adminUserSession.isAdmin;
	const allowedTabs = canConfigure ? [TabValues.Configure, TabValues.Manage] : [TabValues.Manage];
	const defaultTab = needsConfiguration && canConfigure ? TabValues.Configure : TabValues.Manage;
	const activeTab = allowedTabs.find(tab => tab === params.tab) ?? defaultTab;
	const urlHasValidTab = activeTab === params.tab;

	React.useEffect(() => {
		if (!integrationQuery.data || urlHasValidTab) {
			return;
		}

		changeTab(defaultTab);
	}, [changeTab, defaultTab, integrationQuery.data, urlHasValidTab]);

	const onConnect = async () => {
		if (!apiKey.trim()) {
			toast.error('Please enter your DonorPerfect API key.');
			return;
		}
		authMutation.mutate({ apiKey: apiKey.trim() });
	};

	if (integrationQuery.isLoading) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const hasAuthError = authMutation.error;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(styleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={donorPerfectLogo} alt='DonorPerfect Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with DonorPerfect to pull donor information, including names, email and physical mailing
				addresses, phone numbers, gifting history, and more.
			</p>

			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={activeTab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					{canConfigure ? (
						<Tabs.Trigger
							value={TabValues.Configure}
							className={`tab-view-tabbar-item ${
								activeTab === TabValues.Configure ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<SuccessCheckIcon
								fillColor={needsConfiguration ? THEME.colors.icon.disabled : undefined}
								ringColor={needsConfiguration ? THEME.colors.background.main : undefined}
							/>
							<span>Configure</span>
						</Tabs.Trigger>
					) : null}

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${
							activeTab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Configure} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						{needsConfiguration || isEditing ? (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									{needsConfiguration
										? 'To get started, enter the API key provided by DonorPerfect Support or their API Help Desk.'
										: 'Enter a new API key to update your DonorPerfect connection.'}
								</p>
								<div className={css(styleSheet.signInContainer)}>
									<div className={css(styleSheet.signInForm)}>
										<div className={css(styleSheet.inputWrapper)}>
											<Label htmlFor='donor-perfect-api-key' styles={[styleSheet.signInFormFieldLabel]}>
												API Key
											</Label>
											<div className={css(styleSheet.signInFormField)}>
												<Input
													autoComplete='off'
													autoFocus={true}
													id='donor-perfect-api-key'
													onChange={ev => setApiKey(ev.target.value)}
													type='password'
													value={apiKey}
													styles={[styleSheet.textInput]}
													aria-invalid={!!hasAuthError}
													aria-describedby={hasAuthError ? 'donor-perfect-api-key-error' : undefined}
												/>
												{hasAuthError && <DangerIcon backgroundColor={THEME.colors.background.light} />}
											</div>
											{hasAuthError ? (
												<span
													id='donor-perfect-api-key-error'
													role='alert'
													className={css(styleSheet.signInFormFieldError)}
												>
													{authMutation.error?.systemMessage || 'Unable to verify this API key. Please try again.'}
												</span>
											) : null}
										</div>
									</div>
									<div className={css(bs.horizontalStack)}>
										<Button isLoading={authMutation.isPending} onClick={onConnect} variant='primary'>
											{needsConfiguration ? 'Connect' : 'Save'}
										</Button>
										{isEditing ? (
											<Button
												disabled={authMutation.isPending}
												onClick={() => {
													setIsEditing(false);
													setApiKey('');
													authMutation.reset();
												}}
												variant='outline'
											>
												Cancel
											</Button>
										) : null}
									</div>
								</div>
							</>
						) : (
							<>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									Your DonorPerfect account is connected and configured to sync with Levitate.
								</p>
								<div className={css(bs.horizontalStack)}>
									<Button disabled={disconnectMutation.isPending} onClick={() => setIsEditing(true)} variant='primary'>
										Update API Key
									</Button>
									<Button
										isLoading={disconnectMutation.isPending}
										onClick={() => setIsDisconnectModalOpen(true)}
										variant='outline'
									>
										Disconnect
									</Button>
								</div>
							</>
						)}
					</div>
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							{isConnected
								? 'Your DonorPerfect donors will show up in the contacts tab, and their gifts on the donations dashboard.'
								: 'Once you are configured, your DonorPerfect donors will show up in the contacts tab.'}
						</p>
						<Button asChild={true}>
							<Link to='/people'>Contacts</Link>
						</Button>
					</div>
				</Tabs.Content>
			</Tabs.Root>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isDisconnectModalOpen,
					onRequestClose: onDisconnectModalRequestClose,
				}}
				options={disconnectModalOptions}
				title='Disconnect DonorPerfect? Levitate will stop syncing donor data from your DonorPerfect account. You can reconnect at any time by entering your API key again.'
			/>
		</div>
	);
}
